/**
 * Audit log — admin view of every state-changing action on the society.
 *
 * Filter by action / role / entity type. Cursor-paginated by `at` (descending).
 * Row click → drill dialog showing before/after JSON diffs.
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ScrollText,
  Filter,
  Activity,
  CalendarClock,
  UserCircle2,
  Clock3,
  Download
} from 'lucide-react';
import { get, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { formatDateTime } from '@/lib/utils';

interface AuditLogEntry {
  id: string;
  society_id: string | null;
  actor_user_id: string | null;
  actor_role: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  user_agent: string | null;
  at: string;
}

interface ListResponse {
  items: AuditLogEntry[];
  next_cursor: string | null;
}

interface Facet {
  value: string;
  count: number;
}

interface FacetsResponse {
  actions: Facet[];
  roles: Facet[];
  entity_types: Facet[];
}

const ROLE_VARIANT: Record<string, 'info' | 'warn' | 'alert' | 'ok' | 'muted'> = {
  admin: 'info',
  super_admin: 'alert',
  resident: 'ok',
  guard: 'warn'
};

function ActionPill({ action }: { action: string }): JSX.Element {
  const [entity, verb] = action.includes('.') ? action.split('.') : [action, ''];
  return (
    <span className="inline-flex items-center gap-1 rounded-vt-sm bg-vt-surface-2 px-2 py-0.5 text-vt-12 text-vt-text">
      <span className="text-vt-text-muted">{entity}</span>
      {verb ? <span className="text-vt-primary font-medium">.{verb}</span> : null}
    </span>
  );
}

function JsonPreview({ data }: { data: Record<string, unknown> | null }): JSX.Element {
  if (!data || Object.keys(data).length === 0) {
    return <span className="text-vt-text-muted">—</span>;
  }
  return (
    <pre className="max-h-[420px] overflow-auto rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-12 text-vt-text">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

/** UUID v1-v5 shape — used to gate the entity_id query (the API rejects non-UUIDs with 422). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function AuditLogPage(): JSX.Element {
  const [action, setAction] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [entityType, setEntityType] = useState<string>('');
  const [entityIdInput, setEntityIdInput] = useState<string>('');
  const [entityId, setEntityId] = useState<string>(''); // debounced + UUID-validated
  const [drillId, setDrillId] = useState<string | null>(null);
  // Each extra page carries the rows AND the cursor that yielded them, so the
  // next "Load more" uses the LAST fetched cursor (not whatever useQuery just refetched).
  const [extraPages, setExtraPages] = useState<AuditLogEntry[]>([]);
  const [moreCursor, setMoreCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [exporting, setExporting] = useState(false);

  // Debounce entity_id input → only fire query when input stops changing 300ms
  // AND is either empty or a valid UUID. Sending a partial string would 422.
  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = entityIdInput.trim();
      if (trimmed === '' || UUID_RE.test(trimmed)) {
        setEntityId(trimmed);
      } else {
        // Invalid (partial) UUID — treat as no filter rather than firing 422.
        setEntityId('');
      }
    }, 300);
    return () => clearTimeout(t);
  }, [entityIdInput]);

  const facets = useQuery({
    queryKey: ['audit-log', 'facets'],
    queryFn: () => get<FacetsResponse>('/audit-log/facets'),
    staleTime: 60_000
  });

  // First page only — cursor is NEVER in the queryKey, so changing filters
  // refetches but loading more does not duplicate-fetch the first page.
  const list = useQuery({
    queryKey: ['audit-log', action, role, entityType, entityId],
    queryFn: () =>
      get<ListResponse>('/audit-log', {
        params: {
          limit: 50,
          ...(action ? { action } : {}),
          ...(role ? { actor_role: role } : {}),
          ...(entityType ? { entity_type: entityType } : {}),
          ...(entityId ? { entity_id: entityId } : {})
        }
      }),
    refetchOnWindowFocus: false
  });

  // Whenever the first page (re)loads, reset accumulated extra pages and seed
  // pagination state from this fresh first response.
  useEffect(() => {
    if (list.data) {
      setExtraPages([]);
      setMoreCursor(list.data.next_cursor);
      setHasMore(Boolean(list.data.next_cursor));
    }
  }, [list.data]);

  const firstPage = list.data?.items ?? [];
  // Belt-and-braces dedupe in case server returns boundary-timestamp overlap.
  const allItems = useMemo(() => {
    const seen = new Set<string>();
    const out: AuditLogEntry[] = [];
    for (const r of [...firstPage, ...extraPages]) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }
    return out;
  }, [firstPage, extraPages]);
  const drillEntry = useMemo(
    () => (drillId ? allItems.find((e) => e.id === drillId) ?? null : null),
    [drillId, allItems]
  );

  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    // first-page useQuery refetches on key change; the useEffect above resets pagination.
  };

  const onLoadMore = async () => {
    if (!moreCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await get<ListResponse>('/audit-log', {
        params: {
          limit: 50,
          ...(action ? { action } : {}),
          ...(role ? { actor_role: role } : {}),
          ...(entityType ? { entity_type: entityType } : {}),
          ...(entityId ? { entity_id: entityId } : {}),
          cursor: moreCursor
        }
      });
      // Dedupe by id — cursor on `at` DESC can repeat boundary rows when two
      // entries share the same timestamp.
      setExtraPages((prev) => {
        const seen = new Set<string>([...firstPage, ...prev].map((r) => r.id));
        return [...prev, ...next.items.filter((r) => !seen.has(r.id))];
      });
      setMoreCursor(next.next_cursor);
      setHasMore(Boolean(next.next_cursor));
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const onExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
      const apiPrefix = import.meta.env.VITE_API_PREFIX ?? '/v1';
      const params = new URLSearchParams();
      if (action) params.set('action', action);
      if (role) params.set('actor_role', role);
      if (entityType) params.set('entity_type', entityType);
      if (entityId) params.set('entity_id', entityId);
      const qs = params.toString();
      const url = `${apiUrl}${apiPrefix}/audit-log/export.csv${qs ? `?${qs}` : ''}`;

      const token = useAuthStore.getState().accessToken;
      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (!res.ok) {
        // Try to parse JSON error body for nicer message
        let msg = `Export failed (${res.status})`;
        try {
          const body = (await res.json()) as { error?: { code?: string; message?: string } };
          if (body?.error?.message) msg = body.error.message;
        } catch {
          /* non-JSON body */
        }
        toast.error(msg);
        return;
      }

      const blob = await res.blob();
      // Derive filename from Content-Disposition if present
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m?.[1] ?? `hooddesk-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success('Export downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const kpiTotal = facets.data?.actions.reduce((s, a) => s + a.count, 0) ?? 0;
  const kpiActions = facets.data?.actions.length ?? 0;
  const kpiRoles = facets.data?.roles.length ?? 0;

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every state-changing action — append-only, tamper-evident."
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Activity className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Total events</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {facets.isLoading ? <Skeleton className="h-7 w-12" /> : kpiTotal}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <ScrollText className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Distinct actions</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {facets.isLoading ? <Skeleton className="h-7 w-12" /> : kpiActions}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-vt-warn">
                <UserCircle2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Actor roles</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {facets.isLoading ? <Skeleton className="h-7 w-12" /> : kpiRoles}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter row */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
            <div className="flex items-center gap-vt-2 self-center">
              <Filter className="h-4 w-4 text-vt-text-muted" />
              <span className="text-vt-12 text-vt-text-muted">Filters:</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-vt-11 text-vt-text-muted">Action</span>
              <Select
                value={action}
                onChange={(e) => onFilterChange(setAction)(e.target.value)}
                style={{ minWidth: 180 }}
              >
                <option value="">All actions</option>
                {facets.data?.actions.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.value} ({a.count})
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-vt-11 text-vt-text-muted">Actor role</span>
              <Select
                value={role}
                onChange={(e) => onFilterChange(setRole)(e.target.value)}
                style={{ minWidth: 140 }}
              >
                <option value="">All roles</option>
                {facets.data?.roles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.value} ({r.count})
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-vt-11 text-vt-text-muted">Entity type</span>
              <Select
                value={entityType}
                onChange={(e) => onFilterChange(setEntityType)(e.target.value)}
                style={{ minWidth: 140 }}
              >
                <option value="">All entities</option>
                {facets.data?.entity_types.map((et) => (
                  <option key={et.value} value={et.value}>
                    {et.value} ({et.count})
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-vt-11 text-vt-text-muted">Entity ID</span>
              <Input
                type="search"
                value={entityIdInput}
                onChange={(e) => setEntityIdInput(e.target.value)}
                placeholder="UUID (exact match)"
                style={{ minWidth: 280 }}
                aria-invalid={
                  entityIdInput.trim() !== '' && !UUID_RE.test(entityIdInput.trim())
                    ? true
                    : undefined
                }
              />
            </div>
            {action || role || entityType || entityIdInput ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAction('');
                  setRole('');
                  setEntityType('');
                  setEntityIdInput('');
                  setEntityId('');
                }}
              >
                Clear
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void onExportCsv()}
              disabled={exporting}
              className="ml-auto"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.75} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {list.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <EmptyState
                icon={<ScrollText className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load audit log"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : allItems.length === 0 ? (
              <EmptyState
                icon={<ScrollText className="h-6 w-6" strokeWidth={1.75} />}
                title="No audit events"
                description={
                  action || role || entityType || entityId
                    ? 'No events match the current filters.'
                    : 'No state-changing actions have been recorded yet.'
                }
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allItems.map((e) => (
                      <TableRow
                        key={e.id}
                        onClick={() => setDrillId(e.id)}
                        className="cursor-pointer transition-colors hover:bg-vt-surface-2"
                      >
                        <TableCell className="whitespace-nowrap text-vt-text-muted">
                          <Clock3 className="mr-1 inline h-3.5 w-3.5 -translate-y-0.5" />
                          {formatDateTime(e.at)}
                        </TableCell>
                        <TableCell>
                          <ActionPill action={e.action} />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <Badge variant={ROLE_VARIANT[e.actor_role] ?? 'muted'}>{e.actor_role}</Badge>
                            {e.actor_user_id ? (
                              <span className="mt-0.5 font-mono text-vt-11 text-vt-text-muted">
                                {e.actor_user_id.slice(0, 8)}…
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-vt-text">{e.entity_type}</span>
                            {e.entity_id ? (
                              <span className="font-mono text-vt-11 text-vt-text-muted">
                                {e.entity_id.slice(0, 8)}…
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-vt-11 text-vt-text-muted">{e.ip ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <CursorPaginator
                  hasMore={hasMore}
                  loading={loadingMore}
                  itemsShown={allItems.length}
                  pageSize={50}
                  onLoadMore={() => void onLoadMore()}
                />
                {!hasMore && allItems.length > 0 ? (
                  <div className="mt-vt-4 text-center text-vt-12 text-vt-text-muted">
                    End of log — no more entries.
                  </div>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drill-down dialog */}
      <Dialog open={Boolean(drillId)} onOpenChange={(v) => (v ? null : setDrillId(null))}>
        <DialogContent className="max-w-3xl">
          {drillEntry ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-vt-2">
                  <ActionPill action={drillEntry.action} />
                </DialogTitle>
                <DialogDescription>
                  <CalendarClock className="mr-1 inline h-3.5 w-3.5 -translate-y-0.5" />
                  {formatDateTime(drillEntry.at)} · {drillEntry.actor_role}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-vt-4 py-vt-2">
                <div className="grid grid-cols-2 gap-vt-3 text-vt-12">
                  <div>
                    <div className="text-vt-text-muted">Actor user id</div>
                    <div className="font-mono text-vt-text">{drillEntry.actor_user_id ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-vt-text-muted">IP</div>
                    <div className="font-mono text-vt-text">{drillEntry.ip ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-vt-text-muted">Entity</div>
                    <div className="text-vt-text">
                      {drillEntry.entity_type}
                      {drillEntry.entity_id ? (
                        <span className="ml-1 font-mono text-vt-11 text-vt-text-muted">
                          {drillEntry.entity_id}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <div className="text-vt-text-muted">User agent</div>
                    <div className="truncate text-vt-text" title={drillEntry.user_agent ?? ''}>
                      {drillEntry.user_agent ?? '—'}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-1 text-vt-12 font-semibold uppercase tracking-wider text-vt-text-muted">
                    Before
                  </h4>
                  <JsonPreview data={drillEntry.before} />
                </div>
                <div>
                  <h4 className="mb-1 text-vt-12 font-semibold uppercase tracking-wider text-vt-text-muted">
                    After
                  </h4>
                  <JsonPreview data={drillEntry.after} />
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
